// BookTickets/SummaryBox/useSummaryData.ts
import { useMemo, useCallback } from 'react'
import { useBookingState } from '@booking/state/booking.context'

import { groupBy } from 'lodash'
import { useLocale, useTranslations } from 'next-intl'

import { dateFnsLocale, format as formatInLisbonTz } from '@booking/lib/date'
import { calcSessionTotal, cartHasLines, calcTotalForCart, ticketCount } from '@booking/lib/cart'
import { formatPrice } from '@booking/lib/money'
import { addDays, endOfDay, startOfDay } from 'date-fns'
import { stageNumberOf } from '../../../lib/bookingConfig'

export const useSummaryData = () => {
  const locale = useLocale()
  const t = useTranslations('booking')
  const state = useBookingState()
  const config = state.config
  const cart = state.cart
  const quantities = state.quantities
  const session = state.session
  const stage = state.stage ?? 1
  const shake = useMemo(
    () => ticketCount(quantities) > 0 || Boolean(session),
    [quantities, session],
  )

  const shouldDiscountPrice = useMemo(() => {
    const endOfTomorrow = endOfDay(addDays(new Date(), 1))
    const sessionStart = cart && cartHasLines(cart) ? cart.session.dateTime : session?.dateTime

    // discount only if session time is after end of tomorrow (advance booking discount)
    return sessionStart ? startOfDay(sessionStart) > endOfTomorrow : false
  }, [cart, session])

  const hasTickets = useMemo(
    () =>
      (stage >= stageNumberOf(config, 'customer') && cartHasLines(cart)) ||
      ticketCount(quantities) > 0,
    [cart, quantities, stage, config],
  )

  const hasVouchers = useMemo(
    () => cartHasLines(cart) && cart!.lines.some((line) => !!line.voucher),
    [cart],
  )

  // Derived ticket start
  const getTicketStart = useCallback(
    () =>
      stage >= stageNumberOf(config, 'customer') && cartHasLines(cart)
        ? cart?.session.dateTime
        : session?.dateTime,
    [cart, session, stage, config],
  )

  const ticketDate = useMemo(() => {
    const start = getTicketStart()
    if (!start) return ''
    const dateFormat = locale === 'en' ? 'do MMM yyyy' : 'dd MMMM yyyy'
    return formatInLisbonTz(start, dateFormat, { locale: dateFnsLocale(locale) })
  }, [locale, getTicketStart])

  const ticketTime = useMemo(() => {
    const start = getTicketStart()
    if (!start) return ''
    const timeFormat = locale === 'en' ? 'HH:mm aaa' : 'HH:mm'
    return formatInLisbonTz(start, timeFormat, { locale: dateFnsLocale(locale) })
  }, [locale, getTicketStart])

  const hasTicketDate = stage >= stageNumberOf(config, 'datetime') && Boolean(getTicketStart())
  const hasTicketTime = stage >= stageNumberOf(config, 'datetime') && Boolean(getTicketStart())

  const ticketLines = useMemo(() => {
    if (!cart || stage <= stageNumberOf(config, 'datetime')) {
      return quantities
        .filter((item) => item.quantity > 0)
        .map((item) => ({
          variant: item.category,
          quantity: item.quantity,
          price: undefined,
          normalPrice: undefined,
        }))
    }

    const grouped = groupBy(
      cart.lines.filter((line) => line.category && !line.voucher /*&& !line.physical*/),
      'category',
    )

    const ticketLines = Object.entries(grouped).map(([id, group]) => {
      const disc = config.ticketPriceFunc ? config.ticketPriceFunc : null
      let normalPrice = disc ? disc(group[0]?.unitValue) : null

      // dont discount price for default flow if not advanced booking
      if (config.context === 'default' && !shouldDiscountPrice) {
        normalPrice = null
      }

      return {
        variant: id,
        quantity: group.length,
        normalPrice: normalPrice,
        price: group[0]?.unitValue ?? 0,
      }
    })

    const voucherLines = cart.lines
      .filter((line) => line.voucher)
      .map((line) => ({
        variant: line.category,
        quantity: 1,
        price: 0,
        normalPrice: 16 * 100,
        hasVoucher: true,
      }))

    /*const physicalLines = cart.lines
      .filter((l) => l.physical)
      .map((line) => ({
        variant: line.productId,
        quantity: line.quantity,
        price: line.unitPrice,
      }))*/

    console.log(ticketLines)
    return [...ticketLines, ...voucherLines /*...physicalLines*/]
  }, [cart, quantities, stage, config, shouldDiscountPrice])

  const extrasLines = useMemo(() => {
    const extrasConfig = state.config?.extras
    if (!extrasConfig) return []

    if (stage <= stageNumberOf(config, 'datetime') || !cart) {
      return state.extras
        .filter((e) => e.quantity > 0)
        .map((e) => {
          const item = extrasConfig.items?.find((i) => i.category === e.category)
          return {
            variant: e.category,
            quantity: e.quantity,
            label: item ? t(item.labelKey) : e.category,
          }
        })
    }

    return cart.extraLines.map((line) => {
      const item = extrasConfig.items?.find((i) => i.category === line.category)
      return {
        variant: line.id,
        quantity: line.quantity,
        normalPrice: item?.price,
        price: line.unitValue ?? 0,
        label: item ? t(item.labelKey) : '',
      }
    })
  }, [state.extras, state.config?.extras, cart, config, stage, t])

  const hasExtras = extrasLines.length > 0
  const hasAddons = useMemo(
    () => (cart && cart.addons && cart?.addons.length > 0 ? true : false),
    [cart],
  )

  const ticketTotal = useCallback(() => {
    if (!cartHasLines(cart) && session)
      return formatPrice(calcSessionTotal({ session, quantities }))
    return cart ? formatPrice(calcTotalForCart(cart) * 100) : formatPrice(0)
  }, [cart, quantities, session])

  /*const shouldDiscount = useMemo(() => {
    const endOfTomorrow = endOfDay(addDays(new Date(), 1))
    const sessionStart = cart && cartHasLines(cart) ? cart.session.dateTime : session?.dateTime
    if (!sessionStart) return false

    // discount only if session time is after end of tomorrow (advance booking discount)
    return startOfDay(sessionStart) > endOfTomorrow
  }, [cart, session])*/

  const subTotal = useCallback(() => {
    const total = cart ? cart.total : 0

    const cartDiscount = cart?.discount ? (cart.discount.percentage ?? 0) : 0

    // add the discount value
    let sub = (total / (100 - cartDiscount)) * 100

    // add $3 * the amount of tickets
    sub = sub + 3 * (cart?.lines.length ?? 0)

    return formatPrice(sub * 100)
  }, [cart])

  const subTotalBeforeVoucher = subTotal

  const discountValue = () => {
    const discount = shouldDiscountPrice ? 10 : 0
    const cartDiscount = cart?.discount ? (cart.discount.percentage ?? 0) : 0
    return discount + cartDiscount + '%'
  }

  /*const discountPriceMap = () => {
    const map = [
      { adv: 23.5, normal: 26.5 },
      { adv: 24.5, normal: 27.5 },
      { adv: 25.5, normal: 28.5 },
      { adv: 26.5, normal: 29.5 },
      { adv: 27.5, normal: 30.5 },
      { adv: 28.5, normal: 31.5 },
    ]
  }*/

  return {
    shake,
    ticketLines,
    hasTickets,
    hasVouchers,
    hasTicketDate,
    ticketDate,
    hasTicketTime,
    ticketTime,
    extrasLines,
    hasAddons,
    hasExtras,
    ticketTotal,
    subTotal,
    subTotalBeforeVoucher,
    discountValue,
    shouldDiscountPrice,
    //availableFields,
  }
}

export type UseSummaryData = ReturnType<typeof useSummaryData>
